import { getFromWkt } from './generated';
import type { Client as CoordinatesClient } from './generated/client';

const CSIB_BASE64_PREFIX = 'CsibBase64:';
const OGC_WKT_PREFIX = 'OgcWkt:';

export interface CrsResolverLogger {
  warn(message: string, context?: object, error?: Error): void;
}

export class CrsResolver {
  private readonly wktToCsibCache = new Map<
    string,
    Promise<string | undefined>
  >();

  constructor(
    private readonly coordinatesClient: CoordinatesClient,
    private readonly logger?: CrsResolverLogger
  ) {}

  public async resolveCsib(
    crs: string | undefined
  ): Promise<string | undefined> {
    if (!crs) {
      return undefined;
    }

    if (crs.startsWith(CSIB_BASE64_PREFIX)) {
      const csib = crs.slice(CSIB_BASE64_PREFIX.length);
      return csib.length > 0 ? csib : undefined;
    }

    if (crs.startsWith(OGC_WKT_PREFIX)) {
      const wkt = crs.slice(OGC_WKT_PREFIX.length);
      if (wkt.length === 0) {
        return undefined;
      }
      return this.resolveCsibFromWkt(wkt);
    }

    return undefined;
  }

  private resolveCsibFromWkt(wkt: string): Promise<string | undefined> {
    const cached = this.wktToCsibCache.get(wkt);
    if (cached !== undefined) {
      return cached;
    }

    const promise = this.fetchCsibFromWkt(wkt).then(
      (csib) => {
        if (csib === undefined) {
          this.wktToCsibCache.delete(wkt);
        }
        return csib;
      },
      (error: unknown) => {
        this.logger?.warn(
          'CrsResolver: failed to resolve OGC WKT to CSIB; rendering without georeference',
          undefined,
          error instanceof Error ? error : new Error(String(error))
        );
        this.wktToCsibCache.delete(wkt);
        return undefined;
      }
    );

    this.wktToCsibCache.set(wkt, promise);
    return promise;
  }

  private async fetchCsibFromWkt(wkt: string): Promise<string | undefined> {
    const response = await getFromWkt({
      body: wkt,
      client: this.coordinatesClient,
    });

    if (response.error) {
      throw new Error(
        `Coordinates service rejected WKT: ${stringifyError(response.error)}`
      );
    }

    const csibBytes = response.data?.csib;
    if (!csibBytes || csibBytes.length === 0) {
      return undefined;
    }

    return bytesToBase64(csibBytes);
  }
}

function stringifyError(error: unknown): string {
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}

const BASE64_CHUNK_SIZE = 8192;
const BYTE_MASK = 0xff;

function bytesToBase64(bytes: ReadonlyArray<number>): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK_SIZE) {
    const chunk = bytes.slice(i, i + BASE64_CHUNK_SIZE);
    const charCodes = chunk.map((b) => b & BYTE_MASK);
    binary += String.fromCharCode(...charCodes);
  }
  return btoa(binary);
}
